import { Observable } from 'rxjs';
import { isEqual, difference, } from 'underscore';

import {
    BlacklistedHandle,
    MediaSubscriptionApi,
} from '../../../api/mediaSubscriptions/models/media-subscription.api.model';
import { MediaSubscriptionKeywordsModel } from './media-subscription-keywords.model';
import { MediaSubscriptionStatusEnum } from '../enums/media-subscription-status.enum';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import {
    MediaSubscriptionMediaTypeEnum,
    mediaRequestTypeToStringMap,
    subscriptionTypeIconMap,
    MediaRequestType
} from '../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MentionSearchModel } from './mention-search.model';
import { MediaSubscriptionBackfill } from '../../MediaSubscriptions/models/media-subscription-backfill.model';
import { MediaTypeModel } from './media-type.model';
import { GeoCircleModel } from '../../../Common/Misc/models/geographical-circle.model';
import { IncludeExcludeModel } from '../../../Common/Misc/models/include-exclude-model';
import { HandleHelper } from '../../../Common/Forms/helpers/handle.helper';
import { deepCompare } from '../../../Core/utils';
import { MediaTypeEnum } from '../../../Common/Basic/enums/media-type.enum';
import { BaseModel } from '../../../Common/Basic/models/base.model';
import { LeftSidebarListItemConvertableModel } from '../../../Common/Layout/models/left-sidebar-list-item-convertable.model';
import { LeftSidebarListItem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';

export enum TvEyesMediaTypes {
    all,
    tv,
    radio
}

export interface MediaTypeRequest {
    mediaType: MediaRequestType;

    // tv & radio types don't have handles
    handles: string[];

    // used for agents only
    isModeratedForKeywords?: boolean;
    isModeratedForHandles?: boolean;
    blacklistedHandles?: BlacklistedHandle[];
}

/**
 * Note: This model is shared by 2 different services, media-subscriptions.service.ts & agent.service.ts.
 */
export class MediaSubscription extends BaseModel<MediaSubscription, MediaSubscriptionApi> implements AuditableModel, LeftSidebarListItemConvertableModel {
    id: number;
    name: string;

    type: MediaSubscriptionMediaTypeEnum;
    status: MediaSubscriptionStatusEnum;

    isAgent: boolean;

    // AM subscription only has 1 mediaType defined in mediaTypeRequests
    // except in case of tvEyes.all, which has 2 (TV & Radio)
    mediaTypeRequests?: MediaTypeRequest[] = [];

    /** @deprecated use {@link mediaTypeRequests} */
    mediaTypes?: MediaSubscriptionMediaTypeEnum[] = [];

    tvEyesMediaType: TvEyesMediaTypes = TvEyesMediaTypes.all;

    handlesName: string;
    keywords: MediaSubscriptionKeywordsModel;

    backfills: MediaSubscriptionBackfill[] = [];

    createdByUserId?: number;
    createdDate?: Date;

    modifiedByUserId?: number;
    modifiedDate?: Date;

    hidden: boolean;
    groupId: number;
    isFavorite: boolean;

    // remaining fields are agent only

    isPartiallyModerated?: boolean;
    isModeratedForKeywords?: boolean;
    isModeratedForHandles?: boolean;
    partiallyModeratedMedias?: MediaRequestType[] = [];
    partiallyModeratedNetworks: string;
    moderatedMediaTypes: any;
    themeIds?: number[] = [];

    // Twitter only
    samplePercentage?: number;
    countryCodes?: string[];
    geoCircles?: GeoCircleModel[];
    verifiedAuthor?: boolean;
    languageFilter?: IncludeExcludeModel<string>;


    constructor(apiEntity?: MediaSubscriptionApi) {
        super();
        if (apiEntity) {
            this.constructFromApiModel(apiEntity);
        } else {
            this.constructDefault();
        }
    }

    /**
     * This returns all the media types supported by media subscription
     */
    static getAllMediaType(): MediaTypeEnum[] {
        return [
            MediaTypeEnum.TV,
            MediaTypeEnum.RADIO,
            MediaTypeEnum.REDDIT,
            MediaTypeEnum.TWITTER,
            MediaTypeEnum.FACEBOOK,
            MediaTypeEnum.INSTAGRAM,
            MediaTypeEnum.YOUTUBE,
            MediaTypeEnum.PODCAST,
            MediaTypeEnum.TIKTOK,
            MediaTypeEnum.LINKEDIN,
            MediaTypeEnum.BLUESKY
        ];
    }

    static getCleanedHandles(mediaType: MediaRequestType, handles: string[]): string[] {
        if (!handles?.length) {
            return [];
        }

        let res = [];
        switch (mediaType) {
            case MediaTypeEnum.FACEBOOK:
                res = handles.map(x => HandleHelper.sanitizeFacebookHandle(x));
                break;
            case MediaSubscriptionMediaTypeEnum.twitter:
                res = handles.map(x => HandleHelper.sanitizeTwitterHandle(x));
                break;
            case MediaTypeEnum.INSTAGRAM:
                res = handles.map(x => HandleHelper.sanitizeInstagramHandle(x));
                break;
            case MediaTypeEnum.TIKTOK:
                res = handles.map(x => HandleHelper.sanitizeTikTokHandle(x));
                break;
            case MediaTypeEnum.YOUTUBE:
                res = handles.map(x => HandleHelper.sanitizeYoutubeChannel(x));
                break;
            case MediaTypeEnum.REDDIT:
                res = handles.map(x => HandleHelper.sanitizeRedditUser(x));
                break;
            case MediaSubscriptionMediaTypeEnum.subReddit:
                res = handles.map(x => HandleHelper.sanitizeSubReddit(x));
                break;
            case MediaTypeEnum.LINKEDIN:
                res = handles.map(x => HandleHelper.sanitizeLinkedInHandle(x));
                break;
            case MediaTypeEnum.BLUESKY:
                res = handles.map(x => HandleHelper.sanitizeBlueskyHandle(x));
                break;
        }

        return res;
    }


    constructFromApiModel(apiEntity: MediaSubscriptionApi) {
        this.id = apiEntity.id;
        this.name = apiEntity.label;
        this.isAgent = apiEntity.isAgent;

        this.status = apiEntity.state;

        this.keywords = new MediaSubscriptionKeywordsModel({
            mustTerms: apiEntity.mustTerms,
            shouldTerms: apiEntity.shouldTerms,
            mustNotTerms: apiEntity.mustNotTerms,
            mediaTypeRequests: apiEntity.mediaTypeRequests,
            handles: apiEntity.handles,
            isAgent: apiEntity.isAgent,
        });

        this.mediaTypes = [];
        this.mediaTypeRequests = apiEntity.mediaTypeRequests;
        this.applyPrefixToHandles(this.mediaTypeRequests);

        this.createdByUserId = apiEntity.createdUserId;
        this.createdDate = apiEntity.createdDate ? new Date(apiEntity.createdDate): null;

        this.modifiedByUserId = apiEntity.modifiedUserId;
        this.modifiedDate = apiEntity.modifiedDate ? new Date(apiEntity.modifiedDate) : null;

        this.hidden = apiEntity.hidden;
        this.isFavorite = apiEntity.isFavorited;
        this.groupId = apiEntity.displayGroupId;

        if (apiEntity.backfills) {
            this.backfills = apiEntity.backfills.map(apiModel => new MediaSubscriptionBackfill(apiModel));
        } else {
            this.backfills = [];
        }


        if (this.isAgent) {
            this.isPartiallyModerated = this.isPartialModeration(apiEntity);
            this.partiallyModeratedNetworks = this.formatModeratedNeworks(this.partiallyModeratedMedias);
            this.themeIds = apiEntity.themeIds;

            // Twitter only
            this.samplePercentage = apiEntity.samplePercentage;
            this.countryCodes = apiEntity.countryCodes;
            this.geoCircles = apiEntity.geoCircles;
            this.verifiedAuthor = apiEntity.verifiedAuthor;
            this.languageFilter = apiEntity.languageFilter;
        } else {
            // AM subscription scenario
            const apiMediaTypes: MediaRequestType[] = [];
            // mediaType and mediaTypes are deprecated, here we use mediaTypeRequests
            if (this.mediaTypeRequests?.length) {
                this.mediaTypeRequests.forEach(item => apiMediaTypes.push(item.mediaType));
            } else if (apiEntity.mediaTypes?.length) {
                // back compatibility, fall back to mediaTypes
                apiEntity.mediaTypes.forEach(item => apiMediaTypes.push(item as MediaRequestType));
            }

            if (apiMediaTypes.length) {
                const hasTv = apiMediaTypes.includes(MediaTypeEnum.TV);
                const hasRadio = apiMediaTypes.includes(MediaTypeEnum.RADIO);
                if (hasTv || hasRadio) {
                    this.type = MediaSubscriptionMediaTypeEnum.broadcast;
                    if (hasTv && hasRadio) {
                        this.tvEyesMediaType = TvEyesMediaTypes.all;
                    } else if (hasTv) {
                        this.tvEyesMediaType = TvEyesMediaTypes.tv;
                    } else {
                        this.tvEyesMediaType = TvEyesMediaTypes.radio;
                    }
                } else {
                    // maps cleanly since broadcast is handled above
                    this.type = apiMediaTypes[0] as MediaSubscriptionMediaTypeEnum;
                }
            }

            // back comatibility, if this.type cannot be found in mediaTypeRequests, use apiEntity.mediaType
            if (!this.type) {
                this.type = apiEntity.mediaType;
            }
        }
    }


    constructDefault() {
        this.isAgent = false;
        this.type = MediaSubscriptionMediaTypeEnum.facebook;
        this.keywords = new MediaSubscriptionKeywordsModel();
        this.status = MediaSubscriptionStatusEnum.PAUSED;
        this.createdByUserId = null;
        this.createdDate = null;
        this.modifiedByUserId = null;
        this.modifiedDate = null;
        this.isFavorite = false;
        this.hidden = false;
    }

    toApiModel(): MediaSubscriptionApi {
        const apiEntity: MediaSubscriptionApi = {
            id: this.id,
            label: this.name,

            isAgent: this.isAgent,
            state: this.status,

            // mediaType, mediaTypes, and handles are deprecated
            mediaType: null,
            mediaTypes: [],
            handles: [],

            mediaTypeRequests: this.toApiMediaTypeRequests(this.mediaTypeRequests),

            // Copy by value
            shouldTerms: Object.assign([], this.keywords.or),
            mustTerms: Object.assign([], this.keywords.and),
            mustNotTerms: Object.assign([], this.keywords.not),

            themeIds: this.themeIds,

            createdDate: this.createdDate,
            createdUserId: this.createdByUserId,

            modifiedUserId: this.modifiedByUserId,
            modifiedDate: this.modifiedDate,

            hidden: this.hidden,
            displayGroupId: this.groupId,
            isFavorited: this.isFavorite,

            // Twitter only
            samplePercentage: this.samplePercentage,
            countryCodes: this.countryCodes,
            geoCircles: this.geoCircles,
            verifiedAuthor: this.verifiedAuthor,
            languageFilter: this.languageFilter
        };

        /** @TODO move logic into component */
        if (this.isAgent) {
            // when we create/edit social topics, we do not want to send mediaTypes & mediaType
            delete apiEntity.mediaTypes;
            delete apiEntity.mediaType;

            // if there's no keywords, remove media types with no handles
            if (
                this.keywords.or.length === 0 &&
                this.keywords.and.length === 0 &&
                this.keywords.not.length === 0
            ) {
                apiEntity.mediaTypeRequests =
                    apiEntity.mediaTypeRequests.filter(request => request.handles.length !== 0);
            }
        }

        return apiEntity;
    }

    /**
    * Checks if the media subscription is saved or not
    * by checking if the ID exists
    */
    isSaved(): boolean {
        const isSaved: boolean = this.id !== null && this.id !== undefined;

        return isSaved;
    }

    /**
    * Checks if the name has been filled out
    */
    hasName(): boolean {
        const whitespace: RegExp = /\s/g;
        const isNameSet: boolean = this.name !== null && this.name !== undefined;
        const isNameComplete: boolean = isNameSet && this.name.replace(whitespace, '').length > 0;
        const hasName: boolean = isNameSet && isNameComplete;

        return hasName;
    }

    equals(other: MediaSubscription): boolean {
        if (!other) {
            return false;
        }
        if (this.name !== other.name) {
            return false;
        }

        if (this.isAgent !== other.isAgent) {
            return false;
        }

        if (this.type !== other.type) {
            return false;
        }

        if (this.status !== other.status) {
            return false;
        }

        if (!this.keywords.equals(other.keywords)) {
            return false;
        }

        if (this.tvEyesMediaType !== other.tvEyesMediaType) {
            return false;
        }

        if (this.mediaTypes?.length !== other.mediaTypes?.length) {
            return false;
        } else if (!isEqual(this.mediaTypes?.slice().sort(), other.mediaTypes?.slice().sort())) {
            return false;
        }

        if (this.isAgent && !deepCompare(
            this.mediaTypeRequests.slice().sort((a, b) => a.mediaType.localeCompare(b.mediaType)),
            other.mediaTypeRequests.slice().sort((a, b) => a.mediaType.localeCompare(b.mediaType)))
        ) {
            return false;
        }

        if (difference(this.themeIds, other.themeIds)?.length > 0) {
            return false;
        }

        if (this.isFavorite !== other.isFavorite) {
            return false;
        }

        if (this.hidden !== other.hidden) {
            return false;
        }

        return true;
    }

    toMentionSearchModel(): MentionSearchModel {
        const mentionSearchModel = new MentionSearchModel();

        mentionSearchModel.searchCriteria.query.subscription.include = [this.id];
        if (this.keywords) {
            mentionSearchModel.searchCriteria.query.keywordGroups = [this.keywords.toMonitoringKeywordGroupModel()];
        }

        mentionSearchModel.searchCriteria.query.includedMediaTypes = [
            ...MediaSubscription.getAllMediaType()
        ].map(val => new MediaTypeModel(val));

        return mentionSearchModel;
    }



    getHandlesName() {
        switch (this.type) {
            case MediaSubscriptionMediaTypeEnum.facebook:
            case MediaSubscriptionMediaTypeEnum.twitter:
            case MediaSubscriptionMediaTypeEnum.instagram:
            case MediaSubscriptionMediaTypeEnum.tiktok:
            case MediaSubscriptionMediaTypeEnum.bluesky:
                this.handlesName = 'Monitoring.mediaSubscriptions.handles';
                break;
            case MediaSubscriptionMediaTypeEnum.reddit:
            case MediaSubscriptionMediaTypeEnum.youtube:
                this.handlesName = 'Monitoring.mediaSubscriptions.userProfiles';
                break;
        }
        return this.handlesName;
    }

    isModeratedByQuota(): boolean {
        return this.type !== MediaSubscriptionMediaTypeEnum.broadcast && this.type !== MediaSubscriptionMediaTypeEnum.podcast;
    }


    clone(): MediaSubscription {
        return new MediaSubscription(this.toApiModel());
    }


    cloneForCopy(): MediaSubscription {
        const clone = this.clone();
        delete clone.id;
        clone.status = MediaSubscriptionStatusEnum.PAUSED;
        clone.createdByUserId = null;
        clone.createdDate = null;
        clone.modifiedByUserId = null;
        clone.modifiedDate = null;
        clone.name = null;
        clone.hidden = false;
        return clone;
    }


    hasHandles(): boolean {
        if (!this.mediaTypeRequests?.length) {
            return false;
        }

        let hasHandles = false;

        this.mediaTypeRequests.forEach(request => {
            if (request.handles?.length) {
                hasHandles = true;
            }
        });

        return hasHandles;
    }

    toLeftSidebarListItem(buttons?: LeftSidebarListItemButton[]): LeftSidebarListItem {
        const item = new LeftSidebarListItem();

        item.value = this.id;
        item.label = this.name;
        item.icon = subscriptionTypeIconMap.get(this.type);
        item.iconAriaLabel = `MediaSubscriptionType.${this.type}`;
        item.status = `MediaSubscriptions.status.${this.status}`;
        item.groupId = this.groupId;
        item.isFavorite = this.isFavorite;

        // Use last modified date if it has a value, if not use created date
        item.lastModifiedDate = +((this.modifiedDate) ? this.modifiedDate : this.createdDate);

        if (this.hidden) {
            buttons = buttons.filter(button => button.key !== ActionsEnum.hide);
        } else {
            buttons = buttons.filter(button => button.key !== ActionsEnum.show);
        }

        if (this.isFavorite) {
            buttons = buttons.filter(button => button.key !== ActionsEnum.favorite);
        } else {
            buttons = buttons.filter(button => button.key !== ActionsEnum.unfavorite);
        }

        item.buttons = buttons;

        return item;
    }

    private isPartialModeration(mediaSubscriptionApiModel: MediaSubscriptionApi): boolean {
        let isPartiallyModerated: boolean = false;
        const mediaTypeRequests = mediaSubscriptionApiModel.mediaTypeRequests;
        if (!this.moderatedMediaTypes) {
            this.moderatedMediaTypes = {};
        }
        if (mediaTypeRequests) {
            for (const mediaTypeRequest of mediaTypeRequests) {
                this.moderatedMediaTypes[mediaTypeRequest.mediaType] = mediaTypeRequest;
                if (mediaTypeRequest.isModeratedForHandles || mediaTypeRequest.isModeratedForKeywords) {
                    isPartiallyModerated = true;
                    this.partiallyModeratedMedias.push(mediaTypeRequest.mediaType);
                }
                if (mediaTypeRequest.isModeratedForHandles) {
                    this.isModeratedForHandles = true;
                }
                if (mediaTypeRequest.isModeratedForKeywords) {
                    this.isModeratedForKeywords = true;
                }
            }
        }
        return isPartiallyModerated;
    }

    private formatModeratedNeworks(partiallyModeratedMedias: MediaRequestType[]): string {
        let partiallyModeratedTooltip: string = '';

        for (let i = 0; i < partiallyModeratedMedias.length; i++ ) {
            partiallyModeratedTooltip += '-' + mediaRequestTypeToStringMap.get(partiallyModeratedMedias[i]) + '\n';
        }

        return partiallyModeratedTooltip;
    }

    private toApiMediaTypeRequests(mediaTypeRequests?: MediaTypeRequest[]): MediaTypeRequest[] {
        if (!mediaTypeRequests?.length) {
            return null;
        }

        return mediaTypeRequests.map(request => {
            return({
                mediaType: request.mediaType,
                handles: MediaSubscription.getCleanedHandles(request.mediaType, request.handles),
                isModeratedForKeywords: request.isModeratedForKeywords,
                isModeratedForHandles: request.isModeratedForHandles,
                blacklistedHandles: request.blacklistedHandles
            });
        });

    }

    private applyPrefixToHandles(mediaTypeRequests: MediaTypeRequest[]) {
        if (!mediaTypeRequests?.length || !this.isAgent) {
            return;
        }

        mediaTypeRequests.forEach(request => {
            request.handles = MediaSubscription.getCleanedHandles(request.mediaType, request.handles);
            switch (request.mediaType) {
                case MediaTypeEnum.INSTAGRAM:
                case MediaSubscriptionMediaTypeEnum.twitter:
                case MediaTypeEnum.YOUTUBE:
                case MediaTypeEnum.TIKTOK:
                case MediaTypeEnum.BLUESKY:
                    request.handles = request.handles?.map(handle => '@' + handle);
                    break;
                case MediaTypeEnum.REDDIT:
                    request.handles = request.handles?.map(handle => 'u/' + handle);
                    break;
                case MediaSubscriptionMediaTypeEnum.subReddit:
                    request.handles = request.handles?.map(handle => 'r/' + handle);
                    break;
                default:
                    break;
            }
        });
    }
}
